import { BookOpen, ChevronDown } from "lucide-react";
import { useState } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaTabs } from "@/shared/components/navigation/ColocaTabs";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import {
  ORGANIZATION_WEBHOOK_EVENT_CATALOG,
  type OrganizationWebhookEventType,
} from "../../services/organizationWebhooksService";
import { eventTypeLabel } from "./webhookLabels";

export interface WebhookIntegrationGuideProps {
  /** The header the secret travels in, quoted verbatim in the samples. */
  secretHeader: string;
}

/**
 * One envelope per business group, taken from the backend contract
 * (`docs/features/customer-webhook-events.md`) and trimmed to the fields a reader needs to recognise the
 * resource — `…` marks what was left out. `data` is the resource as its public `GET /:id` returns it, so the
 * full field list lives in the API reference.
 */
const GROUP_EXAMPLES: Partial<Record<OrganizationWebhookEventType, string>> = {
  payments: `{
  "id": "1f0e9a52-6c1b-4b8e-9f0a-3d2c1b0a9e8f",
  "type": "payments",
  "event": "payin_created",
  "created_at": "2026-09-23T15:00:00.120Z",
  "data": {
    "id": "7b1c2d3e-4f5a-6b7c-8d9e-0f1a2b3c4d5e",
    "status": "CREATED",
    "rail": "PIX",
    "currency": "BRL",
    "gross_amount": "500.00000000",
    "net_amount": "495.00000000",
    "idempotency_key": "order-88231",
    "qr_code_payload": "00020126580014br.gov.bcb.pix…",
    "expires_at": "2026-09-23T15:30:00.000Z",
    …
  }
}`,
  identities: `{
  "id": "2b3c4d5e-6f7a-4b8c-9d0e-1f2a3b4c5d6e",
  "type": "identities",
  "event": "kyc_approved",
  "created_at": "2026-09-23T21:10:00.200Z",
  "data": {
    "id": "6a7b8c9d-0e1f-4a2b-8c3d-4e5f6a7b8c9d",
    "type": "individual",
    "country": "COL",
    "identification_type": "CC",
    "identification_number": "1020304050",
    "kyc_status": "VERIFIED",
    "kyc_verified_at": "2026-09-23T21:09:58.000Z",
    "kyc_rejection_reason": null,
    "email": "laura.gomez@example.com",
    …
  }
}`,
  cards: `{
  "id": "7c8d9e0f-1a2b-4c3d-8e4f-5a6b7c8d9e0f",
  "type": "cards",
  "event": "card_transaction_authorized",
  "created_at": "2026-09-23T22:15:03.400Z",
  "data": {
    "id": "0d1e2f3a-4b5c-4d6e-8f7a-8b9c0d1e2f3a",
    "card_id": "a9b8c7d6-e5f4-4a3b-8c2d-1e0f9a8b7c6d",
    "amount": "45000.00",
    "currency": "COP",
    "usd_amount": "11.25",
    "transaction_type": "PURCHASE",
    "status": "COMPLETED",
    "merchant_name": "CAFE MOLIENDA",
    …
  }
}`,
  wallets: `{
  "id": "4e5f6a7b-8c9d-4e0f-9a1b-2c3d4e5f6a7b",
  "type": "wallets",
  "event": "wallet_created",
  "created_at": "2026-09-23T20:00:00.050Z",
  "data": {
    "id": "8f9a0b1c-2d3e-4f4a-8b5c-6d7e8f9a0b1c",
    "currency": "COP",
    "status": "active",
    "balance_available": "0.00000000",
    "balance_pending": "0.00000000",
    "balance_blocked": "0.00000000",
    "created_at": "2026-09-23T20:00:00.000Z"
  }
}`,
};

/** A titled block of prose, with optional bullets and a code sample under it. */
function GuideSection({
  step,
  title,
  body,
  bullets,
  code,
}: {
  step?: string;
  title: string;
  body: string;
  bullets?: string[];
  code?: string;
}) {
  return (
    <section className="border-t border-border px-4 py-3.5 first:border-t-0">
      <h4 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
        {step && (
          <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-primary/10 font-mono text-[10px] text-primary">
            {step}
          </span>
        )}
        {title}
      </h4>
      <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{body}</p>
      {bullets && (
        <ul className="m-0 mt-1.5 list-none space-y-1.5 p-0 text-[12px] leading-relaxed text-muted-foreground">
          {bullets.map((bullet) => (
            <li key={bullet}>· {bullet}</li>
          ))}
        </ul>
      )}
      {code && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-muted/60 p-3 font-mono text-[11.5px] leading-relaxed text-foreground">
          {code}
        </pre>
      )}
    </section>
  );
}

/**
 * The integration contract, in the screen where it is acted on.
 *
 * It is collapsed by default and lives inline rather than behind a link, because the two things it explains
 * are the two things that surprise an integrator here: that Coloca requires the secret to be **echoed back**
 * on the handshake (a 200 is not enough), and that rotating the secret or changing the URL re-runs that
 * handshake and pauses the endpoint if the receiver has not caught up. Both are discovered otherwise by
 * having deliveries stop.
 */
export function WebhookIntegrationGuide({ secretHeader }: WebhookIntegrationGuideProps) {
  const { t } = useTranslations("settings");
  const [open, setOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-foreground/5"
      >
        <span className="grid h-[28px] w-[28px] shrink-0 place-items-center rounded-md border border-border bg-muted/40 text-muted-foreground">
          <BookOpen size={14} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-foreground">{t("webhooks_guide_title")}</span>
          <span className="block text-[11.5px] text-muted-foreground">{t("webhooks_guide_sub")}</span>
        </span>
        <ChevronDown
          size={16}
          className={cn("shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="border-t border-border bg-card">
          {/* First, because the handshake runs AT registration: a secret the
              customer's server does not have yet cannot be echoed, and the
              endpoint lands paused. This is the step that decides whether
              registering works on the first attempt. */}
          <GuideSection
            step="1"
            title={t("webhooks_guide_setup_title")}
            body={t("webhooks_guide_setup_body")}
            bullets={[
              t("webhooks_guide_setup_own"),
              t("webhooks_guide_setup_generated"),
              t("webhooks_guide_setup_format"),
            ]}
            code={`# Your server's configuration, before registering the URL here\nCOLOCA_WEBHOOK_SECRET=whsec_…`}
          />

          <GuideSection
            step="2"
            title={t("webhooks_guide_receive_title")}
            body={t("webhooks_guide_receive_body", { header: secretHeader })}
            bullets={[
              t("webhooks_guide_receive_dedupe"),
              t("webhooks_guide_receive_snapshot"),
              t("webhooks_guide_receive_order"),
            ]}
            code={`POST /your/webhook-endpoint\nContent-Type: application/json\n${secretHeader}: whsec_…\nUser-Agent: Coloca-Webhooks/1.0\n\n{\n  "id": "9c6b2f6e-…",\n  "type": "payments",\n  "event": "payin_created",\n  "created_at": "2026-09-23T15:04:05.000Z",\n  "data": { "id": "7b1c2d3e-…", "status": "CREATED", … }\n}`}
          />

          <GuideSection
            step="3"
            title={t("webhooks_guide_handshake_title")}
            body={t("webhooks_guide_handshake_body", { header: secretHeader })}
            code={`// Only for type "test" / event "ping"\nconst { type, event } = req.body;\nif (type === "test" && event === "ping") {\n  return res.status(200).json({ secret: req.headers["${secretHeader}"] });\n}\n\n// Every other event: a 2xx is enough, no body required\nreturn res.status(200).json({ received: true });`}
          />

          <GuideSection
            step="4"
            title={t("webhooks_guide_verify_title")}
            body={t("webhooks_guide_verify_body", { header: secretHeader })}
            code={`const expected = process.env.COLOCA_WEBHOOK_SECRET;\nconst received = req.headers["${secretHeader}"];\n\n// Constant-time compare, and reject before doing any work\nif (!received || received !== expected) {\n  return res.status(401).json({ error: "invalid_secret" });\n}`}
          />

          <GuideCatalog />

          <section className="border-t border-border px-4 py-3.5">
            <h4 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
              {t("webhooks_guide_rotation_title")}
              <ColocaBadge variant="warning" className="text-[10px]">
                {t("webhooks_guide_rotation_badge")}
              </ColocaBadge>
            </h4>
            <ul className="mt-1.5 m-0 list-none space-y-1.5 p-0 text-[12px] leading-relaxed text-muted-foreground">
              <li>· {t("webhooks_guide_rotation_rotate")}</li>
              <li>· {t("webhooks_guide_rotation_url")}</li>
              <li>· {t("webhooks_guide_rotation_activate")}</li>
              <li>· {t("webhooks_guide_rotation_order")}</li>
            </ul>
          </section>

          <GuideSection title={t("webhooks_guide_retries_title")} body={t("webhooks_guide_retries_body")} />
        </div>
      )}
    </div>
  );
}

/**
 * The `type → event` catalog, one tab per business group with the events it carries and one envelope as it
 * arrives. `test` is left out: its only event is the handshake ping, which step 3 already covers.
 */
function GuideCatalog() {
  const { t } = useTranslations("settings");

  const tabs = ORGANIZATION_WEBHOOK_EVENT_CATALOG.filter((entry) => entry.type !== "test").map((entry) => ({
    value: entry.type,
    label: eventTypeLabel(t, entry.type),
    content: (
      <div className="mt-2 space-y-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("webhooks_guide_catalog_events")} · type <span className="normal-case">"{entry.type}"</span>
          </p>
          <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-1.5 p-0">
            {entry.events.map((event) => (
              <li key={event}>
                <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                  {event}
                </code>
              </li>
            ))}
          </ul>
        </div>
        {entry.type === "identities" && (
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            · {t("webhooks_guide_catalog_kyb")}
          </p>
        )}
        {GROUP_EXAMPLES[entry.type] && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("webhooks_guide_catalog_example")}
            </p>
            <pre className="mt-1.5 overflow-x-auto rounded-md bg-muted/60 p-3 font-mono text-[11.5px] leading-relaxed text-foreground">
              {GROUP_EXAMPLES[entry.type]}
            </pre>
          </div>
        )}
      </div>
    ),
  }));

  return (
    <section className="border-t border-border px-4 py-3.5">
      <h4 className="text-[13px] font-semibold text-foreground">{t("webhooks_guide_catalog_title")}</h4>
      <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
        {t("webhooks_guide_catalog_body")}
      </p>
      <ColocaTabs tabs={tabs} className="mt-2" listClassName="flex-wrap" />
    </section>
  );
}
